import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Projector, Wand2, GitCompare } from "lucide-react";
import { Button } from "./ui/button";
import { useLang } from "../i18n/LanguageContext";

/* ------------------------------------------------------------------ */
/*  Hero = a real cinema scene:                                        */
/*  dark room -> white screen descends -> projector powers on ->       */
/*  volumetric beam + dust -> screen illuminates -> projected content  */
/* ------------------------------------------------------------------ */

const useIsMobile = () => {
  const [m, setM] = React.useState(typeof window !== "undefined" && window.innerWidth < 768);
  React.useEffect(() => {
    const on = () => setM(window.innerWidth < 768);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return m;
};

const CATS = [
  { key: "home-cinema", labelKey: "hero.catHome", to: "/category/home-cinema", testid: "hero-tile-home-cinema" },
  { key: "gaming", labelKey: "hero.catGaming", to: "/category/gaming", testid: "hero-tile-gaming" },
  { key: "portable", labelKey: "hero.catPortable", to: "/category/portable", testid: "hero-tile-portable" },
  { key: "business", labelKey: "hero.catBusiness", to: "/category/business", testid: "hero-tile-business" },
];

/* deterministic dust field (clipped inside the beam cone) */
const DUST = Array.from({ length: 18 }, (_, i) => ({
  left: 16 + ((i * 37) % 66),
  bottom: 5 + ((i * 53) % 82),
  size: 1 + ((i * 29) % 3) * 0.75,
  dur: 6 + (i % 5) * 2.2,
  delay: (i % 7) * 1.15,
  op: 0.35 + ((i * 13) % 40) / 100,
}));

/* ------------------------------------------------------------------ */
/*  Future video layer (Higgsfield / AI loop). Renders nothing today.  */
/*  Later: <HeroAtmosphere videoSrc="/media/cinema-loop.mp4" />        */
/* ------------------------------------------------------------------ */
export const HeroAtmosphere = ({ videoSrc = null, poster = null }) => {
  if (!videoSrc) return null;
  return (
    <video
      className="absolute inset-0 h-full w-full object-cover opacity-40 pointer-events-none"
      src={videoSrc} poster={poster} autoPlay muted loop playsInline aria-hidden="true"
    />
  );
};

/* ---------------------------- projector --------------------------- */
const ProjectorDevice = ({ lensGlow }) => (
  <div className="relative">
    {/* lens light spill above the housing */}
    <motion.div style={{ opacity: lensGlow }} className="absolute -top-1 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
      <div className="h-2.5 w-16 rounded-full bg-white/80 blur-md" />
      <div className="absolute top-0.5 left-1/2 -translate-x-1/2 h-1.5 w-6 rounded-full bg-white blur-[2px]" />
    </motion.div>
    <svg viewBox="0 0 260 132" className="w-full h-auto" aria-hidden="true">
      <defs>
        <linearGradient id="pj-body" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#323947" />
          <stop offset="0.35" stopColor="#1a1f29" />
          <stop offset="1" stopColor="#0d0f15" />
        </linearGradient>
        <linearGradient id="pj-top" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="rgba(226,238,252,0.55)" />
          <stop offset="1" stopColor="rgba(226,238,252,0)" />
        </linearGradient>
        <linearGradient id="pj-stand" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#171b24" />
          <stop offset="1" stopColor="#0a0c11" />
        </linearGradient>
      </defs>
      {/* floor shadow */}
      <ellipse cx="130" cy="125" rx="94" ry="6" fill="rgba(0,0,0,0.55)" />
      {/* low media stand */}
      <rect x="34" y="97" width="192" height="9" rx="3" fill="url(#pj-stand)" stroke="rgba(255,255,255,0.11)" strokeWidth="1" />
      <rect x="52" y="106" width="7" height="18" rx="2" fill="#0c0e14" />
      <rect x="201" y="106" width="7" height="18" rx="2" fill="#0c0e14" />
      {/* projector body, seen from behind */}
      <rect x="76" y="45" width="108" height="52" rx="11" fill="url(#pj-body)" stroke="rgba(255,255,255,0.16)" strokeWidth="1" />
      {/* lit top edge (light spilling over the housing) */}
      <rect x="84" y="46.5" width="92" height="3" rx="1.5" fill="url(#pj-top)" />
      {/* rear vents */}
      <rect x="94" y="62" width="52" height="2" rx="1" fill="rgba(255,255,255,0.06)" />
      <rect x="94" y="69" width="52" height="2" rx="1" fill="rgba(255,255,255,0.06)" />
      <rect x="94" y="76" width="52" height="2" rx="1" fill="rgba(255,255,255,0.06)" />
      <rect x="94" y="83" width="52" height="2" rx="1" fill="rgba(255,255,255,0.06)" />
      {/* IO ports */}
      <rect x="156" y="64" width="9" height="6" rx="1.5" fill="#05060a" stroke="rgba(255,255,255,0.08)" strokeWidth="0.6" />
      <rect x="156" y="77" width="14" height="6" rx="1.5" fill="#05060a" stroke="rgba(255,255,255,0.08)" strokeWidth="0.6" />
      {/* status LED */}
      <circle cx="90" cy="88" r="2" fill="#36F4A4" opacity="0.9" />
      {/* feet */}
      <rect x="86" y="95" width="10" height="4" rx="2" fill="#05060a" />
      <rect x="164" y="95" width="10" height="4" rx="2" fill="#05060a" />
    </svg>
  </div>
);

/* ------------------------------ scene ----------------------------- */
const CinemaScene = ({ v, t, compact = false }) => {
  const screenW = compact ? "relative w-[88%] max-w-[430px] mx-auto" : "relative w-[min(640px,58vw)] mx-auto";
  return (
    <div className="relative w-full h-full flex flex-col items-center overflow-hidden" data-testid="hero-cinema-scene">
      <HeroAtmosphere />
      {/* room */}
      <div className="absolute inset-0 cinema-room" />
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(50% 34% at 50% 24%, rgba(205,222,244,0.05), transparent 70%)" }} />
      <div className="absolute left-0 top-[6%] bottom-[22%] w-[13%] acoustic-panel pointer-events-none" />
      <div className="absolute right-0 top-[6%] bottom-[22%] w-[13%] acoustic-panel acoustic-panel-r pointer-events-none" />
      <div className="absolute left-[2.5%] top-[28%] h-32 w-16 sconce pointer-events-none" />
      <div className="absolute right-[2.5%] top-[28%] h-32 w-16 sconce pointer-events-none" />
      <div className="absolute bottom-0 inset-x-0 h-[20%] cinema-floor pointer-events-none" />
      <div className="absolute inset-0 cinema-vignette pointer-events-none" />
      <div className="absolute inset-0 opacity-[0.04] noise-overlay pointer-events-none" />

      <div className={`relative z-10 flex flex-col items-center w-full h-full ${compact ? "pt-4" : "pt-[8.5vh]"}`}>
        {/* ------------- PROJECTION SCREEN ------------- */}
        <div className={screenW}>
          {/* soft glow escaping around the lit screen */}
          <motion.div
            style={{ opacity: v.hotspot, background: "radial-gradient(60% 60% at 50% 45%, rgba(226,238,252,0.14), transparent 70%)" }}
            className="absolute -inset-10 pointer-events-none"
          />
          {/* casing / roller */}
          <div className="screen-casing relative z-20 -mx-3 h-3.5 rounded-[5px]" data-testid="hero-screen-casing" />
          {/* screen drops out of the casing */}
          <div className="relative z-10 overflow-hidden">
            <motion.div style={{ y: v.screenDropY }} className="relative">
              <div className="relative aspect-video screen-surface overflow-hidden" data-testid="hero-projection-screen">
                {/* projected title card */}
                <motion.div style={{ opacity: v.contentOpacity }} className="absolute inset-0 projected-slide">
                  <div className="absolute inset-x-0 bottom-0 h-1/2 pointer-events-none" style={{ background: "radial-gradient(70% 62% at 50% 100%, rgba(212,168,67,0.15), transparent 72%)" }} />
                  <motion.div style={{ y: v.contentY }} className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
                    <span className="proj-eyebrow">{t("hero.projected")}</span>
                    <h1 className={`${compact ? "text-[27px] leading-[1.06]" : "text-4xl lg:text-6xl leading-[1.02]"} font-display tracking-tight proj-headline mt-2.5`} data-testid="hero-headline">
                      {t("hero.title1")} <span className="proj-headline-em">{t("hero.title2")}</span>
                    </h1>
                    <motion.div
                      style={{ opacity: v.catsOpacity, y: v.catsY }}
                      className={`flex items-center justify-center flex-wrap ${compact ? "gap-x-2.5 gap-y-1.5 mt-3.5" : "gap-x-4 mt-7"}`}
                      data-testid="hero-projected-categories"
                    >
                      {CATS.map((c, i) => (
                        <React.Fragment key={c.key}>
                          {i > 0 && <span className="proj-dot" aria-hidden="true" />}
                          <Link to={c.to} data-testid={c.testid} className="proj-cat">{t(c.labelKey)}</Link>
                        </React.Fragment>
                      ))}
                    </motion.div>
                  </motion.div>
                </motion.div>
                {/* light hotspot on the canvas */}
                <motion.div style={{ opacity: v.hotspot }} className="absolute inset-0 pointer-events-none screen-hotspot" />
                {/* unlit state: white canvas dimmed by the dark room */}
                <motion.div
                  style={{ opacity: v.screenDim, background: "linear-gradient(180deg, rgba(7,9,14,0.88), rgba(7,9,14,0.8))" }}
                  className="absolute inset-0 pointer-events-none"
                />
                <div className="absolute inset-0 pointer-events-none opacity-[0.05] noise-overlay" />
              </div>
              {/* bottom weight bar */}
              <div className="screen-weight mx-auto h-2 w-[97%] rounded-b-[4px]" />
            </motion.div>
          </div>
        </div>

        {/* ------------- VOLUMETRIC BEAM ------------- */}
        <div className={`relative w-full flex-1 flex justify-center ${compact ? "min-h-[90px]" : "min-h-[110px]"}`}>
          <div className={compact ? "relative w-[88%] max-w-[430px] h-full" : "relative w-[min(640px,58vw)] h-full"}>
            <motion.div style={{ opacity: v.beamOpacity, scaleY: v.beamScale }} className="absolute -inset-y-1 inset-x-0 origin-bottom pointer-events-none" data-testid="hero-beam">
              <div className="absolute inset-y-0 -inset-x-[12%] beam-haze" style={{ animation: "beam-breathe 7s ease-in-out infinite" }} />
              <div className="absolute inset-0 beam-mid" />
              <div className="absolute inset-0 beam-core" />
              <div className="absolute inset-0 beam-ray beam-ray-a" />
              <div className="absolute inset-0 beam-ray beam-ray-b" />
              <div className="absolute inset-0 beam-ray beam-ray-c" />
              <motion.div style={{ opacity: v.dustOpacity }} className="absolute inset-0 beam-dust-mask" data-testid="hero-dust">
                {DUST.map((d, i) => (
                  <span key={i} className="dust-p" style={{ left: `${d.left}%`, bottom: `${d.bottom}%`, width: d.size, height: d.size, opacity: d.op, animationDuration: `${d.dur}s`, animationDelay: `-${d.delay}s` }} />
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* ------------- PROJECTOR ------------- */}
        <div className={`relative z-20 shrink-0 ${compact ? "w-40 -mt-1" : "w-52 -mt-2"}`} data-testid="hero-projector-device">
          <ProjectorDevice lensGlow={v.lensGlow} />
          <motion.div
            style={{ opacity: v.beamOpacity, background: "radial-gradient(ellipse at center, rgba(226,238,252,0.10), transparent 70%)" }}
            className="mx-auto -mt-4 h-7 w-[85%] rounded-full blur-lg pointer-events-none"
          />
        </div>
      </div>
    </div>
  );
};

/* ------------------------------ hero ------------------------------ */
export const CinematicHero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const isMobile = useIsMobile();
  const reduce = useReducedMotion();

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  /* scroll story: room -> screen descends -> lens -> beam -> dust -> lit screen -> headline -> categories */
  const screenDropY = useTransform(scrollYProgress, [0.02, 0.14], ["-103%", "0%"]);
  const lensGlow = useTransform(scrollYProgress, [0.13, 0.19], [0, 1]);
  const beamOpacity = useTransform(scrollYProgress, [0.16, 0.28], [0, 1]);
  const beamScale = useTransform(scrollYProgress, [0.16, 0.3], [0.06, 1]);
  const dustOpacity = useTransform(scrollYProgress, [0.26, 0.36], [0, 1]);
  const screenDim = useTransform(scrollYProgress, [0.28, 0.4], [1, 0]);
  const hotspot = useTransform(scrollYProgress, [0.28, 0.42], [0, 1]);
  const contentOpacity = useTransform(scrollYProgress, [0.36, 0.46], [0, 1]);
  const contentY = useTransform(scrollYProgress, [0.36, 0.46], [16, 0]);
  const catsOpacity = useTransform(scrollYProgress, [0.44, 0.54], [0, 1]);
  const catsY = useTransform(scrollYProgress, [0.44, 0.54], [10, 0]);
  const scrollHint = useTransform(scrollYProgress, [0.18, 0.3], [1, 0]);

  const scrollV = { screenDropY, lensGlow, beamOpacity, beamScale, dustOpacity, screenDim, hotspot, contentOpacity, contentY, catsOpacity, catsY };
  /* powered-on scene for mobile / reduced motion */
  const staticV = { screenDropY: "0%", lensGlow: 1, beamOpacity: 1, beamScale: 1, dustOpacity: 1, screenDim: 0, hotspot: 1, contentOpacity: 1, contentY: 0, catsOpacity: 1, catsY: 0 };

  /* ---------- MOBILE: dedicated composition, no pinning ---------- */
  if (isMobile || reduce) {
    return (
      <section className="relative overflow-hidden" data-testid="hero-section-mobile" style={{ minHeight: "100dvh" }}>
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, ease: "easeOut" }}
          className="relative flex flex-col"
          style={{ height: "100dvh", minHeight: "620px", paddingTop: "calc(4.5rem + env(safe-area-inset-top))" }}
        >
          <div className="relative flex-1 min-h-[320px]">
            <CinemaScene v={staticV} t={t} compact />
          </div>
          <div className="relative z-10 shrink-0 px-5 pt-5 text-center bg-gradient-to-b from-transparent to-background" style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto">{t("hero.subtitle")}</p>
            <div className="mt-5 flex flex-col gap-2.5 max-w-sm mx-auto">
              <Button asChild size="lg" className="btn-glow border-0 h-12 text-base">
                <Link to="/shop" data-testid="hero-primary-cta"><Projector className="h-5 w-5 mr-1" /> {t("common.shopProjectors")}</Link>
              </Button>
              <Button asChild size="lg" variant="secondary" className="h-12 text-base hairline">
                <Link to="/advisor" data-testid="hero-secondary-cta"><Wand2 className="h-5 w-5 mr-1 text-cyanx" /> {t("common.findProjector")}</Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </section>
    );
  }

  /* ---------- DESKTOP: sticky scroll story ---------- */
  return (
    <section ref={ref} className="relative h-[250vh]" data-testid="hero-section">
      <div className="sticky top-0 h-screen flex flex-col">
        <div className="flex-1 relative min-h-0">
          <CinemaScene v={scrollV} t={t} />
        </div>
        <div className="relative z-30 pb-8 px-4 bg-gradient-to-b from-transparent to-background/60">
          <div className="container-lumeo text-center">
            <p className="mb-4 text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">{t("hero.subtitle")}</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button asChild size="lg" className="btn-glow border-0 h-12 px-6 text-base">
                <Link to="/shop" data-testid="hero-primary-cta"><Projector className="h-5 w-5 mr-1" /> {t("common.shopProjectors")}</Link>
              </Button>
              <Button asChild size="lg" variant="secondary" className="h-12 px-6 text-base hairline">
                <Link to="/advisor" data-testid="hero-secondary-cta"><Wand2 className="h-5 w-5 mr-1 text-cyanx" /> {t("common.findProjector")}</Link>
              </Button>
              <Button asChild size="lg" variant="ghost" className="h-12 px-5 text-base text-foreground/80">
                <Link to="/compare"><GitCompare className="h-5 w-5 mr-1" /> {t("common.compareModels")}</Link>
              </Button>
            </div>
            <motion.p style={{ opacity: scrollHint }} className="mt-5 font-mono text-[11px] uppercase tracking-widest text-muted-foreground animate-glow-pulse">
              &#8595; {t("hero.scroll")}
            </motion.p>
          </div>
        </div>
      </div>
    </section>
  );
};
